import { useCallback, useEffect, useMemo, useState } from "react";
import { listActionCategories } from "../api/actions.api";
import { listTaxonomyValues } from "../api/taxonomy.api";

export const DEFAULT_ACTION_CATEGORIES = ["Internal Audit", "Control", "Meeting", "Risks"];
export const ACTION_CATEGORY_OPTIONS_CHANGED_EVENT = "action-category-options-changed";

export function useActionCategoryOptions(currentValue = "") {
  const [categories, setCategories] = useState(DEFAULT_ACTION_CATEGORIES);

  const loadCategories = useCallback(async (isActive = () => true) => {
    try {
      const [taxonomyResult, actionCategoryResult] = await Promise.allSettled([
        listTaxonomyValues("action_category"),
        listActionCategories(),
      ]);

      const taxonomyPayload =
        taxonomyResult.status === "fulfilled"
          ? taxonomyResult.value?.data || taxonomyResult.value
          : [];
      const configuredCategories = (Array.isArray(taxonomyPayload) ? taxonomyPayload : [])
        .filter((item) => String(item?.status || "active").toLowerCase() === "active")
        .map((item) => String(item?.label || item?.value || "").trim())
        .filter(Boolean);

      const actionCategoryPayload =
        actionCategoryResult.status === "fulfilled"
          ? actionCategoryResult.value?.data || actionCategoryResult.value
          : [];
      const detectedCategories = (Array.isArray(actionCategoryPayload) ? actionCategoryPayload : [])
        .map((item) => String(item || "").trim())
        .filter(Boolean);

      const mergedCategories = Array.from(new Set([...configuredCategories, ...detectedCategories]));

      if (isActive() && mergedCategories.length > 0) {
        setCategories(mergedCategories);
      }
    } catch (_err) {
      if (isActive()) {
        setCategories(DEFAULT_ACTION_CATEGORIES);
      }
    }
  }, []);

  useEffect(() => {
    let active = true;
    const isActive = () => active;

    loadCategories(isActive);

    const handleCategoriesChanged = () => {
      try {
        loadCategories(isActive);
      } catch (_err) {
        // Best effort refresh only.
      }
    };

    window.addEventListener(ACTION_CATEGORY_OPTIONS_CHANGED_EVENT, handleCategoriesChanged);
    return () => {
      active = false;
      window.removeEventListener(ACTION_CATEGORY_OPTIONS_CHANGED_EVENT, handleCategoriesChanged);
    };
  }, [loadCategories]);

  return useMemo(() => {
    const trimmedCurrentValue = String(currentValue || "").trim();
    if (!trimmedCurrentValue || categories.includes(trimmedCurrentValue)) {
      return categories;
    }
    return [trimmedCurrentValue, ...categories];
  }, [categories, currentValue]);
}
